import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, ArrowRight, Search } from "lucide-react";
import { useSettingsSearch } from "../context/SettingsSearchContext";
import { getInterviews } from "../services/api";
import { getUser, fetchUserById } from "../services/auth";
import { useRBAC } from "../context/RBACContext";
import { RESOURCE_DISPLAY_NAMES, ACTION_NAMES } from "../utils/resourceMapping";
import "./Dashboard.css";

export default function DashboardTopbar({ profileUser }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const settingsSearch = useSettingsSearch();
  const isSettings = pathname === "/settings";
  const { hasResourcePermissionByName } = useRBAC();
  const canReport = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.PROCTORING_ANALYSIS, ACTION_NAMES.GET_DATA,
  );
  const canInterviews = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.VIEW_INTERVIEWS, ACTION_NAMES.GET_DATA,
  );
  const [localUser, setLocalUser] = useState(getUser);
  const [menu, setMenu] = useState("");
  const [alerts, setAlerts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const headerRef = useRef(null);
  const user = profileUser === undefined ? localUser : profileUser;
  const name = `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.name || "User";

  useEffect(() => {
    if (profileUser !== undefined) return;
    let alive = true;
    fetchUserById().then(value => { if (alive && value) setLocalUser(value); }).catch(() => {});
    return () => { alive = false; };
  }, [profileUser]);

  useEffect(() => {
    setMenu("");
    if (!canInterviews && !canReport) return;
    let alive = true;
    setLoading(true);
    setError(false);
    getInterviews(null, null, "desc", null, true).then(response => {
      if (alive) setAlerts((response.data || []).filter(interview => interview.proctoringAlert === true).length);
    }).catch(() => { if (alive) setError(true); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [pathname, canInterviews, canReport]);

  useEffect(() => {
    const close = event => {
      if (event.key === "Escape" || (event.type === "pointerdown" && !headerRef.current?.contains(event.target))) setMenu("");
    };
    document.addEventListener("keydown", close);
    document.addEventListener("pointerdown", close);
    return () => {
      document.removeEventListener("keydown", close);
      document.removeEventListener("pointerdown", close);
    };
  }, []);

  return (
    <header className="ttd-topbar" ref={headerRef} style={{ flexShrink: 0 }}>
      {isSettings && settingsSearch && <div className="ttd-settings-search">
        <Search size={16} aria-hidden="true" />
        <input type="search" aria-label="Find a settings section" placeholder="Search settings..."
          value={settingsSearch.tabSearch} onChange={event => settingsSearch.setTabSearch(event.target.value)} />
      </div>}
      <div className="ttd-top-actions">
        <button
          aria-label="Proctoring notifications"
          aria-expanded={false}
          disabled
        >
          <Bell size={21} />
          {!loading && !error && alerts > 0 && <i className="ttd-notification-dot" />}
        </button>
        <button className="ttd-avatar" aria-label="Account details" aria-expanded={menu === "profile"} onClick={() => setMenu(menu === "profile" ? "" : "profile")}>
          {name[0]?.toUpperCase()}
        </button>
      </div>
      {menu && (
        <div
          className={`ttd-top-menu ${menu === "profile" ? "ttd-profile-menu" : ""}`}
        >
          {menu === "profile" ? (
            <>
              <div className="ttd-profile-heading">
                <span className="ttd-profile-avatar" aria-hidden="true">
                  {name[0]?.toUpperCase()}
                </span>
                <div className="ttd-profile-identity">
                  <strong>{name}</strong>
                  <span className="ttd-profile-role">
                    {user?.role?.name ||
                      (typeof user?.role === "string" ? user.role : "Member")}
                  </span>
                </div>
              </div>
              {user?.email && (
                <div className="ttd-profile-email">
                  <span>Email</span>
                  <strong>{user.email}</strong>
                </div>
              )}
            </>
          ) : (
            <>
              <strong>Proctoring alerts</strong>
              <span>
                {!canInterviews && !canReport
                  ? "Alerts unavailable"
                  : loading
                    ? "Loading..."
                    : error
                      ? "Alerts unavailable"
                      : `${alerts} interviews with alerts`}
              </span>
              {canReport && (
                <button
                  onClick={() => {
                    setMenu("");
                    navigate("/proctoring-analysis");
                  }}
                >
                  View report <ArrowRight size={14} />
                </button>
              )}
            </>
          )}
          <button
            className="ttd-top-menu-close"
            onClick={() => setMenu("")}
          >
            Close
          </button>
        </div>
      )}
    </header>
  );
}
